import type { Preset } from '@studio/models/control/preset';
import { useLatestFn, useLazyRef } from '@studio/react-utils/hooks';
import { pick } from 'lodash-es';
import type { ReactElement } from 'react';
import { useMemo } from 'react';
import type { Wiring } from '../types.js';
import { useWiring } from '../wiring.js';
import { Empty } from './contingency.jsx';
import { List } from './list.jsx';
import type { Props } from './types.js';
import { useAnalytics } from '#~/analytics/index.js';
import { EVENT_CATEGORIES, EVENT_NAMES } from '#~/analytics/constants/index.js';
import { usePresetAccess, usePresetChecking } from '#~/control/preset/use-access.js';
import { Availability } from '../availability.jsx';
import type { DeviceProps } from '#~/device/components/index.js';
import { useSelectedVideo } from '#~/room/hooks.js';

/** @internal */
const Inner = ({ wiring, ...props }: Props & { wiring: Wiring }): ReactElement => {
  const { presets, selection } = wiring;
  const { track } = useAnalytics();

  // Stabilize handlers.
  const handleAdd = useLatestFn(() => {
    wiring.addPreset();
  });

  const handleRefresh = useLatestFn(wiring.refreshPreset);

  const handleRemove = useLatestFn((presetId: Preset['id']) => {
    wiring.removePreset(presetId);
    // Track the delete position preset event
    track(EVENT_NAMES.DELETE_POSITION_PRESET, EVENT_CATEGORIES.MOVEMENT_TOOLS);
  });

  const handleReorder = useLatestFn(wiring.reorderPresets);
  const handleSelect = useLatestFn(wiring.selectPreset);
  const handleRename = useLatestFn(wiring.renamePreset);

  const handler = useLazyRef(() => ({
    onAdd: handleAdd,
    onRefresh: handleRefresh,
    onRemove: handleRemove,
    onRename: handleRename,
    onReorder: handleReorder,
    onSelect: handleSelect
  })).current;

  // Be very careful w/ memoization here!
  return (
    <List
      handler={handler}
      presets={useMemo(() => presets.map((µ) => pick(µ, ['id', 'name', 'thumbnail'])), [presets])}
      selectedIds={selection.presetIds}
      {...props}
    />
  );
};

/** Interim guard. */
const Guard = ({ deviceId, ...props }: DeviceProps & Props): ReactElement => {
  const wiring = useWiring(deviceId);

  const access = usePresetAccess(deviceId);
  // Only the presets list shows the status; the group tabs share the same gate.
  const checking = usePresetChecking(deviceId);
  return (
    <Availability access={access} checking={checking}>
      {wiring ? <Inner wiring={wiring} {...props} /> : null}
    </Availability>
  );
};

/** Position `<Presets>` component. */
export const Presets = (props: Props): ReactElement => {
  const [deviceId] = useSelectedVideo();
  return deviceId ? <Guard deviceId={deviceId} {...props} /> : <Empty />;
};
